import React, { useState, useEffect } from 'react';
import { agentsAPI } from '../services/api';
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

const CATEGORIES = ['general', 'support', 'sales', 'technical', 'creative', 'document', 'other'];

const PromptEditorPage = () => {
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mobileShowList, setMobileShowList] = useState(true);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    system_prompt: '',
    temperature: 0.7,
    category: 'general'
  });

  // Load all prompts on mount
  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async (preserveMessage = false) => {
    setIsLoading(true);
    try {
      const response = await agentsAPI.getSystemPrompts();
      setPrompts(response.data?.prompts || []);
      if (!preserveMessage) {
        setMessage({ type: '', text: '' });
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
      setMessage({ type: 'error', text: 'Failed to load prompts' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadPromptDetails = async (filename) => {
    setIsLoading(true);
    try {
      const response = await agentsAPI.getSystemPrompt(filename);
      const data = response.data;
      setSelectedPrompt(data);
      setEditForm({
        name: data.name || '',
        description: data.description || '',
        system_prompt: data.system_prompt || '',
        conversation_guidelines: data.conversation_guidelines || '',
        response_instructions: data.response_instructions || '',
        continuation_guidelines: data.continuation_guidelines || '',
        temperature: data.temperature || 0.7,
        category: data.category || 'general'
      });
      setIsCreating(false);
      setMobileShowList(false);
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Error loading prompt details:', error);
      setMessage({ type: 'error', text: 'Failed to load prompt details' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedPrompt(null);
    setIsCreating(true);
    setMobileShowList(false);
    setEditForm({
      name: '',
      description: '',
      system_prompt: '',
      conversation_guidelines: '',
      response_instructions: '',
      continuation_guidelines: '',
      temperature: 0.7,
      category: 'general'
    });
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) {
      setMessage({ type: 'error', text: 'Prompt name is required' });
      return;
    }
    if (!editForm.system_prompt.trim()) {
      setMessage({ type: 'error', text: 'System prompt content is required' });
      return;
    }

    const action = isCreating ? 'create this system prompt template' : `save changes to "${selectedPrompt?.name || editForm.name}"`;
    if (!window.confirm(`Are you sure you want to ${action}?\n\nPrompt changes can affect agent behavior. Continue only if this is intentional.`)) {
      return;
    }

    setIsSaving(true);
    try {
      if (isCreating) {
        // Create new prompt
        await agentsAPI.createSystemPrompt(editForm);
        setMessage({ type: 'success', text: `Prompt "${editForm.name}" created successfully!` });
        setIsCreating(false);
      } else if (selectedPrompt) {
        // Update existing prompt — also syncs to all agents using this template
        const response = await agentsAPI.updateSystemPrompt(selectedPrompt.filename, editForm);
        const synced = response.data?.agents_synced || 0;
        const syncMsg = synced > 0 ? ` (synced to ${synced} agent${synced > 1 ? 's' : ''})` : '';
        setMessage({ type: 'success', text: `Prompt "${editForm.name}" saved successfully!${syncMsg}` });
      }
      
      // Reload prompts list (preserve success message)
      await loadPrompts(true);
      
      // If created new, select it
      if (isCreating) {
        const newFilename = editForm.name.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') + '.json';
        await loadPromptDetails(newFilename);
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save prompt';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPrompt) return;

    if (!window.confirm(`Final confirmation: permanently delete "${selectedPrompt.name}"?\n\nAgents using this prompt may need to be reconfigured.`)) {
      return;
    }

    setIsSaving(true);
    try {
      await agentsAPI.deleteSystemPrompt(selectedPrompt.filename);
      setMessage({ type: 'success', text: `Prompt "${selectedPrompt.name}" deleted successfully!` });
      setDeleteDialogOpen(false);
      setSelectedPrompt(null);
      setMobileShowList(true);
      setEditForm({
        name: '',
        description: '',
        system_prompt: '',
        conversation_guidelines: '',
        response_instructions: '',
        continuation_guidelines: '',
        temperature: 0.7,
        category: 'general'
      });
      await loadPrompts(true);
    } catch (error) {
      console.error('Error deleting prompt:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete prompt';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating) {
      setIsCreating(false);
      setMobileShowList(true);
      setEditForm({
        name: '',
        description: '',
        system_prompt: '',
        conversation_guidelines: '',
        response_instructions: '',
        continuation_guidelines: '',
        temperature: 0.7,
        category: 'general'
      });
    } else if (selectedPrompt) {
      // Reload original data
      loadPromptDetails(selectedPrompt.filename);
    }
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Sidebar - Prompts List */}
      <div className={`${mobileShowList ? 'flex' : 'hidden'} md:flex w-full md:w-80 bg-white border-r border-gray-200 flex-col`}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">System Prompts</h2>
            <button
              onClick={loadPrompts}
              disabled={isLoading}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Refresh"
            >
              <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <button
            onClick={handleCreateNew}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Prompt
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {isLoading && prompts.length === 0 ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : prompts.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No prompts found</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {prompts.map((prompt) => (
                <li
                  key={prompt.filename}
                  onClick={() => loadPromptDetails(prompt.filename)}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedPrompt?.filename === prompt.filename ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                  }`}
                >
                  <div className="flex items-start">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{prompt.name}</p>
                      <p className="text-xs text-gray-500 truncate">{prompt.description || 'No description'}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                        {prompt.category}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className={`${!mobileShowList ? 'flex' : 'hidden'} md:flex flex-1 flex-col`}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <button
            onClick={() => setMobileShowList(true)}
            className="md:hidden flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            &larr; Back to list
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isCreating ? 'Create New Prompt' : selectedPrompt ? 'Edit Prompt' : 'Prompt Editor'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {isCreating 
                  ? 'Create a new system prompt template' 
                  : selectedPrompt 
                    ? `Editing: ${selectedPrompt.filename}` 
                    : 'Select a prompt to edit or create a new one'}
              </p>
            </div>
            
            {(isCreating || selectedPrompt) && (
              <div className="flex flex-wrap gap-2">
                {selectedPrompt && !isCreating && (
                  <button
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isSaving}
                    className="flex items-center px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <TrashIcon className="h-5 w-5 mr-1" />
                    Delete
                  </button>
                )}
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 mr-1" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <CheckIcon className="h-5 w-5 mr-1" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
          
          {/* Message */}
          {message.text && (
            <div className={`mt-3 p-3 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message.text}
            </div>
          )}
        </div>

        {/* Editor Form */}
        <div className="flex-1 overflow-y-auto p-6">
          {(isCreating || selectedPrompt) ? (
            <div className="max-w-4xl space-y-6">
              {/* Name and Category Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Name *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    disabled={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="e.g., Customer Support Agent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of this prompt's purpose"
                />
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Temperature: {editForm.temperature}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={editForm.temperature}
                  onChange={(e) => setEditForm(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Precise (0)</span>
                  <span>Creative (1)</span>
                </div>
              </div>

              {/* System Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  System Prompt *
                </label>
                <textarea
                  value={editForm.system_prompt}
                  onChange={(e) => setEditForm(prev => ({ ...prev, system_prompt: e.target.value }))}
                  rows={16}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Enter the system prompt that will guide the AI's behavior..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {editForm.system_prompt.length} characters
                </p>
              </div>

              {/* Conversation Guidelines */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conversation Guidelines
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Document grounding rules, response format, and behavioral guardrails appended after the system prompt.
                </p>
                <textarea
                  value={editForm.conversation_guidelines}
                  onChange={(e) => setEditForm(prev => ({ ...prev, conversation_guidelines: e.target.value }))}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="e.g. ## STRICT DOCUMENT GROUNDING RULES..."
                />
              </div>

              {/* Response Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response Instructions
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Short bullet-point instructions appended after the document context section.
                </p>
                <textarea
                  value={editForm.response_instructions}
                  onChange={(e) => setEditForm(prev => ({ ...prev, response_instructions: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="e.g. - ONLY answer using the documents above..."
                />
              </div>

              {/* Continuation Guidelines */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Continuation Guidelines
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Rules applied only on follow-up messages (no greetings, build on context, etc.).
                </p>
                <textarea
                  value={editForm.continuation_guidelines}
                  onChange={(e) => setEditForm(prev => ({ ...prev, continuation_guidelines: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="e.g. - NO GREETINGS on follow-up messages..."
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Prompt Selected</h3>
                <p className="text-gray-500 mb-4">Select a prompt from the list to edit, or create a new one.</p>
                <button
                  onClick={handleCreateNew}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create New Prompt
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Prompt"
        entityName={selectedPrompt?.name || ''}
        warningText="This will permanently delete this system prompt. Any agents using this prompt will need to be reconfigured."
        confirmButtonText="Delete Prompt"
      />
    </div>
  );
};

export default PromptEditorPage;
